import { MOVEABLE_LOCK_CLASS } from 'constants/bounding-box';
import { DEFAULT_SNAP_DATA, Snap, useSmartGuide } from 'hooks/useSmartGuide';
import { selectZoom } from 'modules/Editor/store/editorSettingsSelector';
import { useAppDispatch, useAppSelector } from 'modules/Editor/store/hooks';
import { selectWidget } from 'modules/Editor/store/infographSelector';
import { UpdateWidget, updateWidget } from 'modules/Editor/store/infographSlice';
import {
  WidgetActionState,
  resetPreviousWidgetData,
  setWidgetActionState,
} from 'modules/Editor/store/widgetControlSlice';
import {
  selectActiveWidgetIds,
  selectActiveWidgets,
  selectCropId,
  selectWidgetActionState,
} from 'modules/Editor/store/widgetSelector';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import Selecto from 'react-selecto';
import { PortTypes } from 'types/flowCore.types';
import { WidgetId } from 'types/idTypes';
import { WidgetType } from 'types/widget.types';
import { isCornerHandle, isSideHandle } from 'utils/boundingBox';
import { getElementForWidgetId, isTargetStyleNumber, setWidgetElementWrapperPosition } from 'utils/dom';
import { parseStrictNumber } from 'utils/number';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { hasDragHandle } from 'widgets/sdk/DragHandle';
import { groupIdCache } from 'widgets/sdk/GroupIdCache';

import { useFlow, useFlowPort } from '../FlowCore';
import { useFocus } from '../Focus';
import { NON_GROUP_MEMBER_IDX, PAGE_SCROLL_STEP, PAGE_SCROLL_THRESHOLD } from './BoundingBox.config';
import {
  calculateActualScrollX,
  calculateActualScrollY,
  checkShouldSaveTarget,
  extendWidgetCreator,
  getDirectionAsString,
  getGroupPosition,
  getHideDefaultLines,
  getKeepAspectRatioHandles,
  getNonActiveTargetIdxs,
  getStartFrame,
  getWidgetEdgeDraggable,
  getWidgetElements,
  getWidgetHandles,
  getWidgetPadding,
  getWidgetResizable,
  getWidgetRotatable,
  setHandleStyle,
  unsetHandleStyle,
} from './BoundingBox.helpers';
import { StyledMoveable } from './BoundingBox.style';
import {
  BoundingBoxBaseProps,
  Frame,
  OnClickGroupEvent,
  OnDragEndEvent,
  OnDragEvent,
  OnDragGroupEndEvent,
  OnDragGroupEvent,
  OnDragGroupStartEvent,
  OnDragStartEvent,
  OnResizeEndEvent,
  OnResizeEvent,
  OnResizeGroupEndEvent,
  OnResizeGroupEvent,
  OnResizeGroupStartEvent,
  OnResizeStartEvent,
  OnRotateEndEvent,
  OnRotateEvent,
  OnRotateGroupEndEvent,
  OnRotateGroupEvent,
  OnRotateGroupStartEvent,
  OnRotateStartEvent,
  OnScrollEvent,
  OnScrollGroupEvent,
  PageScroll,
  ResizingState,
  WidgetEvent,
} from './BoundingBox.types';
import {
  ResponsiveShape,
  createResponsiveShape,
  createResponsiveWidgetArr,
  responsiveResizeGroupMembers,
} from './ResponsiveGroup';
import { UpdateBoundingRect } from './UpdateBoundingRect';
import { WidgetSelect } from './WidgetSelect';
import { useBoundingBox, useExtendBoundingBoxWithMoveable } from './useBoundingBox/useBoundingBox';

export const BoundingBox = () => {
  const lastSnapPosition = useRef<Snap | null>();
  const moveableRef: any = useRef(null);
  const selectoRef = useRef<Selecto | null>(null);
  const { pageScrollContainerRef } = useFocus();
  // keep track of the amount of pageScroll on X and Y axis while dragging
  const pageScrollOnDrag = useRef<PageScroll>([0, 0]);
  // keep track of original scrollHeight and scrollWidth for pageScrollContainer when drag starts
  const originalScrollDimensions = useRef<PageScroll>([0, 0]);
  // Keep track whether actual resize dragging started or not and to which direction
  // Used for adding handle styling - hover is not enough if the cursor is moved outside of the handle while dragging resize handle
  const resizeStateRef = useRef<ResizingState>({ actualResizingStarted: false, direction: '' });

  // For responsively resizing responsive group members (if any) when group is resized from the side
  const responsiveShapeRef = useRef<ResponsiveShape | undefined>();

  const [frameMap] = useState<Frame>(() => new Map());

  const activeWidgetIds = useAppSelector(selectActiveWidgetIds);
  const activeWidgets = useAppSelector(selectActiveWidgets);
  const hasMemberWidgets =
    activeWidgets.length === 1 && (!!activeWidgets[0].groupId || !!activeWidgets[0].responsiveGroupId);
  const activeGroupIdx = hasMemberWidgets ? groupIdCache.getGroupIdx(activeWidgets[0].id) : NON_GROUP_MEMBER_IDX;

  const isSingleWidget = activeWidgets.length === 1;
  const selectedWidgetId = isSingleWidget ? activeWidgets[0].responsiveGroupId || activeWidgets[0].id : '';
  const selectedSingleWidget = useAppSelector(selectWidget(selectedWidgetId));
  const zoom = useAppSelector(selectZoom);
  const widgetActionState = useAppSelector(selectWidgetActionState);
  const widgetType = getWidgetTypeFromId(selectedWidgetId);

  const dispatch = useAppDispatch();

  const { renderPort, renderGroupPort, clearPort, creatorPortData } = useFlowPort();
  const {
    startFlowMode,
    stopFlowMode,
    getConnectedLines,
    isActiveWidgetLinesConnected,
    isActiveWidgetsHasLineConnected,
  } = useFlow();

  const isDrag = widgetActionState === WidgetActionState.Drag;
  const widgetHandles = getWidgetHandles(activeWidgets, selectedSingleWidget, isDrag, zoom);
  const isResizable = getWidgetResizable(activeWidgetIds);
  const hideDefaultLines = getHideDefaultLines(activeWidgetIds);
  const keepAspectRatioHandles = getKeepAspectRatioHandles(activeWidgetIds);
  const showDragControl = hasDragHandle(activeWidgets[0]?.id);
  const padding = getWidgetPadding(widgetType);

  // If isAspectRatioLocked is true, will override keepRatio in Moveable props
  const [keepRatio, setKeepRatio] = useState<boolean>(false);

  const isLocked = !!selectedSingleWidget?.isLocked;

  const [setSmartGuideConfig, compute, match, hide] = useSmartGuide(
    document.getElementById('smart-guide-container') as HTMLElement,
    {
      zIndex: 1003,
      enableSnap: true,
      zoomPercent: zoom * 100,
    },
  );

  const { widgetRefs, boundingBox } = useBoundingBox();
  useExtendBoundingBoxWithMoveable({ boundingBox, moveableRef });

  const onClickGroup = (e: OnClickGroupEvent): void => {
    selectoRef?.current?.clickTarget(e.inputEvent, e.inputTarget);
  };

  const saveWidget = useCallback(
    (widgetId: string, widgetData: object): void => {
      if (!widgetId) return;
      if (!widgetData) return;

      dispatch(updateWidget({ widgetId, widgetData }));
    },
    [dispatch],
  );

  const resetActionState = useCallback(() => dispatch(setWidgetActionState(WidgetActionState.Default)), [dispatch]);

  const getPageScrollPosition = pageScrollContainerRef?.current
    ? (e: { scrollContainer: HTMLElement; direction: number[] }): number[] => {
        return [pageScrollContainerRef.current!.scrollTop, pageScrollContainerRef.current!.scrollLeft];
      }
    : undefined;

  const onScroll = (e: OnScrollEvent): void => {
    // only allow page to scroll when dragging for now
    // only allow scroll either on X or Y axis, diagonal scrolling would cause issues with updating widget UI while dragging
    if (widgetActionState !== WidgetActionState.Drag || e.direction[0] * e.direction[1] !== 0) return;

    const pageScrollEl = pageScrollContainerRef?.current;
    const [scrollWidth, scrollHeight] = originalScrollDimensions.current;
    const actualScrollX = calculateActualScrollX(PAGE_SCROLL_STEP, e.direction[0], scrollWidth, pageScrollEl);
    const actualScrollY = calculateActualScrollY(PAGE_SCROLL_STEP, e.direction[1], scrollHeight, pageScrollEl);

    const pageScrollX = e.direction[0] * actualScrollX;
    const pageScrollY = e.direction[1] * actualScrollY;
    if ((Math.abs(pageScrollX) === 0 && Math.abs(pageScrollY) === 0) || !pageScrollEl) return;

    pageScrollOnDrag.current[0] += pageScrollX;
    pageScrollOnDrag.current[1] += pageScrollY;
    pageScrollEl.scrollBy(pageScrollX, pageScrollY);
  };

  const onScrollGroup = (e: OnScrollGroupEvent): void => {
    // only allow page to scroll when dragging for now
    // only allow scroll either on X or Y axis, diagonal scrolling would cause issues with updating widget UI while dragging
    if (widgetActionState !== WidgetActionState.Drag || e.direction[0] * e.direction[1] !== 0) return;

    const pageScrollEl = pageScrollContainerRef?.current;
    const [scrollWidth, scrollHeight] = originalScrollDimensions.current;
    const actualScrollX = calculateActualScrollX(PAGE_SCROLL_STEP, e.direction[0], scrollWidth, pageScrollEl);
    const actualScrollY = calculateActualScrollY(PAGE_SCROLL_STEP, e.direction[1], scrollHeight, pageScrollEl);

    const pageScrollX = e.direction[0] * actualScrollX;
    const pageScrollY = e.direction[1] * actualScrollY;
    if ((Math.abs(pageScrollX) === 0 && Math.abs(pageScrollY) === 0) || !pageScrollEl) return;

    pageScrollOnDrag.current[0] += pageScrollX;
    pageScrollOnDrag.current[1] += pageScrollY;
    pageScrollEl.scrollBy(pageScrollX, pageScrollY);
  };

  const onDragStart = (e: OnDragStartEvent): void => {
    // Drag handle is hidden when the widget is dragged -> cursor style needs to be forced
    document.body.style.cursor = 'move';
    dispatch(setWidgetActionState(WidgetActionState.Drag));

    // Start observing of connected lines
    startFlowMode();

    const frame = getStartFrame(e.target, frameMap);
    e.set(frame.translate);

    // SmartGuide
    lastSnapPosition.current = DEFAULT_SNAP_DATA;
    compute(e, 'drag');

    // PortRenderer
    clearPort();

    pageScrollOnDrag.current = [0, 0];
    if (pageScrollContainerRef?.current)
      originalScrollDimensions.current = [
        pageScrollContainerRef.current.scrollWidth,
        pageScrollContainerRef.current.scrollHeight,
      ];
  };

  const onDrag = (e: OnDragEvent): void => {
    // Prevent port rendering when Select+Drag
    if (creatorPortData !== null) clearPort();

    const target = e.target;
    const frame = frameMap.get(target);
    const pageScrollAdjustment = pageScrollOnDrag.current;
    // SmartGuide
    lastSnapPosition.current = match({ e, pageScrollAdjustment });

    frame.translate = e.beforeTranslate;
    const translateX = `${lastSnapPosition.current?.x ?? frame.translate[0] + pageScrollAdjustment[0] / zoom}px`;
    const translateY = `${lastSnapPosition.current?.y ?? frame.translate[1] + pageScrollAdjustment[1] / zoom}px`;
    const rotate = `${frame.rotate}deg`;
    target.style.transform = `translate(${translateX}, ${translateY}) rotate(${rotate})`;
  };

  const onDragEnd = (e: OnDragEndEvent): void => {
    document.body.style.cursor = '';
    resetActionState();

    // SmartGuide
    hide();
    const target = e.target;
    const frame = frameMap.get(target);
    // Generate connected line data before updating the style
    const connectedLineDatas = getConnectedLines(target.id, target as HTMLElement);
    const pageScrollAdjustment = pageScrollOnDrag.current;
    const newLeftPx =
      parseStrictNumber(target.style.left) +
      (lastSnapPosition.current?.x ?? frame.translate[0] + pageScrollAdjustment[0] / zoom);
    const newTopPx =
      parseStrictNumber(target.style.top) +
      (lastSnapPosition.current?.y ?? frame.translate[1] + pageScrollAdjustment[1] / zoom);

    // Update style
    target.style.left = `${newLeftPx}px`;
    target.style.top = `${newTopPx}px`;
    frame.translate = [0, 0];

    // Reset transform value
    target.style.transform = `translate(${frame.translate[0]}px, ${frame.translate[1]}px) rotate(${frame.rotate}deg)`;

    // Reset pageScrollOnDrag
    pageScrollOnDrag.current = [0, 0];

    if (checkShouldSaveTarget(activeWidgetIds, target)) {
      let batchUpdateWidgetList: UpdateWidget[] = [
        {
          widgetId: target.id,
          widgetData: {
            leftPx: newLeftPx,
            topPx: newTopPx,
          },
        },
      ];
      batchUpdateWidgetList = batchUpdateWidgetList.concat(connectedLineDatas);

      const { isDrag: isDragEvent } = e as OnDragEndEvent;

      // update widget list only if event was a drag event
      if (isDragEvent) {
        dispatch(updateWidget(batchUpdateWidgetList));
        dispatch(resetPreviousWidgetData());
      }
    }

    stopFlowMode();

    // Render the port: line creator only if there are connected lines
    if (connectedLineDatas.length > 0) {
      renderPort(e.target as HTMLElement, PortTypes.Creator);
    }
  };

  const onResizeStart = (e: OnResizeStartEvent): void => {
    dispatch(setWidgetActionState(WidgetActionState.Resize));

    // Start observing of connected lines
    startFlowMode();

    // PortRenderer
    clearPort();

    const frame = getStartFrame(e.target, frameMap);
    if (e.dragStart) e.dragStart.set(frame.translate);

    // SmartGuide
    lastSnapPosition.current = DEFAULT_SNAP_DATA;
    compute(e, 'resize');

    // If direction is specified in keepAspectRatioHandles, then widget should scale
    const direction = getDirectionAsString(e.direction);
    if (
      !!keepAspectRatioHandles &&
      keepAspectRatioHandles.includes?.(direction) &&
      widgetHandles.includes?.(direction)
    ) {
      setKeepRatio(true);
    } else {
      setKeepRatio(false);
    }
  };

  const onResize = (e: OnResizeEvent): void => {
    setHandleStyle(resizeStateRef, e.direction);

    // clear ports when on resize
    clearPort();

    const target = e.target;
    const frame = frameMap.get(target);
    const snapData = match({ e, frame });

    frame.translate = [snapData.x as number, snapData.y as number];
    const currentWidth = parseFloat(target.style.width);
    const isWidthDifferent = currentWidth !== snapData.width;
    // Ignore to update current widget style if value is a string(auto, fit-content, min-content, max-content, etc)
    if (!Number.isNaN(currentWidth) && isWidthDifferent) {
      target.style.width = `${snapData.width}px`;
    }
    const currentHeight = parseFloat(target.style.height);
    const isHeightDifferent = currentHeight !== snapData.height;
    if (!Number.isNaN(currentHeight) && isHeightDifferent) {
      target.style.height = `${snapData.height}px`;
    }

    const isDimensionDifferent = isWidthDifferent || isHeightDifferent;
    if (isDimensionDifferent) {
      const translateX = `${frame.translate[0]}px`;
      const translateY = `${frame.translate[1]}px`;
      const rotate = `${frame.rotate}deg`;
      target.style.transform = `translate(${translateX}, ${translateY}) rotate(${rotate})`;
    }
  };

  const onResizeEnd = (e: OnResizeEndEvent, widgetData = {}): void => {
    resetActionState();

    const target = e.target;
    const frame = frameMap.get(target);

    // Generate connected line data before updating the style
    const connectedLineDatas = getConnectedLines(target.id, target as HTMLElement);

    const lastEvent = e.lastEvent;
    if (lastEvent) {
      /**
       * Use the clientWidth/Height instead of the style.width/height to allow string values of the width/height style.
       * However, DO NOT use the clientTop and clientLeft, it returns unexpected value.
       */
      const newLeftPx = parseStrictNumber(target.style.left) + (lastSnapPosition.current?.x ?? frame.translate[0]);
      const newTopPx = parseStrictNumber(target.style.top) + (lastSnapPosition.current?.y ?? frame.translate[1]);
      const newWidthPx = target.clientWidth;
      const newHeightPx = target.clientHeight;

      // Update style
      target.style.left = `${newLeftPx}px`;
      target.style.top = `${newTopPx}px`;

      // Ignore to update current widget style if value is a string(auto, fit-content, min-content, max-content, etc)
      if (!Number.isNaN(parseFloat(target.style.width))) target.style.width = `${newWidthPx}px`;
      if (!Number.isNaN(parseFloat(target.style.height))) target.style.height = `${newHeightPx}px`;

      // Reset transform value
      frame.translate = [0, 0];
      e.target.style.transform = `translate(${frame.translate[0]}px, ${frame.translate[1]}px) rotate(${frame.rotate}deg)`;

      if (checkShouldSaveTarget(activeWidgetIds, target)) {
        let batchUpdateWidgetList: UpdateWidget[] = [
          {
            widgetId: target.id,
            widgetData: {
              leftPx: newLeftPx,
              topPx: newTopPx,
              widthPx: newWidthPx,
              heightPx: newHeightPx,
              ...widgetData,
            },
          },
        ];
        batchUpdateWidgetList = batchUpdateWidgetList.concat(connectedLineDatas);
        dispatch(updateWidget(batchUpdateWidgetList));
      }

      unsetHandleStyle(resizeStateRef);
    }

    // Reset
    hide();
    stopFlowMode();

    // Render the port: line creator
    if (connectedLineDatas.length > 0) {
      renderPort(e.target as HTMLElement, PortTypes.Creator);
    }
  };

  const onRotateStart = (e: OnRotateStartEvent): void => {
    // Rotation handle is hidden when the widget is rotated -> cursor style needs to be forced
    document.body.style.cursor = 'alias';
    dispatch(setWidgetActionState(WidgetActionState.Rotate));

    // Start observing of connected lines
    startFlowMode();
    // Clear port when start rotating. Will show again when rotate ends.
    clearPort();

    const frame = getStartFrame(e.target, frameMap);
    e.set(frame.rotate);
  };

  const onRotate = (e: OnRotateEvent): void => {
    const target = e.target;
    const frame = frameMap.get(target);
    frame.rotate = e.beforeRotation;

    // Ignore to update current widget style if value is a string(auto, fit-content, min-content, max-content, etc)
    if (!Number.isNaN(parseFloat(target.style.width))) target.style.width = `${e.drag.width}px`;
    if (!Number.isNaN(parseFloat(target.style.height))) target.style.height = `${e.drag.height}px`;

    target.style.transform = `translate(${frame.translate[0]}px, ${frame.translate[1]}px) rotate(${e.beforeRotation}deg)`;
  };

  const onRotateEnd = (e: OnRotateEndEvent): void => {
    document.body.style.cursor = '';
    resetActionState();

    const target = e.target;
    const frame = frameMap.get(target);

    // Generate connected line data before updating the style
    const connectedLineDatas = getConnectedLines(target.id, target as HTMLElement);

    if (checkShouldSaveTarget(activeWidgetIds, target)) {
      let batchUpdateWidgetList: UpdateWidget[] = [
        {
          widgetId: target.id,
          widgetData: {
            rotateDeg: frame.rotate,
          },
        },
      ];
      batchUpdateWidgetList = batchUpdateWidgetList.concat(connectedLineDatas);
      dispatch(updateWidget(batchUpdateWidgetList));
    }

    // Stop listening for flow mode updates
    stopFlowMode();
    // Re-show ports
    if (connectedLineDatas.length > 0) {
      renderPort(e.target as HTMLElement, PortTypes.Creator);
    }
  };

  const onDragGroupStart = (e: OnDragGroupStartEvent): void => {
    dispatch(setWidgetActionState(WidgetActionState.Drag));

    // Start observing of connected lines
    startFlowMode();

    e.events.forEach((ev: OnDragStartEvent) => {
      const frame = getStartFrame(ev.target, frameMap);

      ev.set(frame.translate);
    });

    // SmartGuide
    lastSnapPosition.current = DEFAULT_SNAP_DATA;
    compute(e, 'drag');

    // PortRenderer
    clearPort();

    pageScrollOnDrag.current = [0, 0];
    if (pageScrollContainerRef?.current)
      originalScrollDimensions.current = [
        pageScrollContainerRef.current.scrollWidth,
        pageScrollContainerRef.current.scrollHeight,
      ];
  };

  const onDragGroup = (e: OnDragGroupEvent): void => {
    // NOTE: Prevent port rendering when Select+Drag
    if (creatorPortData !== null) clearPort();
    activeWidgets.forEach((widget) => {
      if (!!widget.groupId && widget.groupMembers.length > 1) {
        // get the group position of the groupId for this widget
        const groupPosition = getGroupPosition(e, frameMap, pageScrollOnDrag, zoom, widget.groupMembers);
        const widgetGroupId = widget.groupId;
        if (groupPosition) {
          setWidgetElementWrapperPosition(widgetGroupId, {
            leftPx: groupPosition.x,
            topPx: groupPosition.y,
            widthPx: groupPosition.width,
            heightPx: groupPosition.height,
          });
        }
      }
    });

    // SmartGuide
    const pageScrollAdjustment = pageScrollOnDrag.current;
    lastSnapPosition.current = match({ e, pageScrollAdjustment });

    const onDragGroupWidget = (ev: OnDragEvent): void => {
      const target = ev.target;
      const frame = frameMap.get(target);
      const pageScrollAdjustment = pageScrollOnDrag.current;

      frame.translate = ev.beforeTranslate;

      const translateX = `${lastSnapPosition.current?.x ?? frame.translate[0] + pageScrollAdjustment[0] / zoom}px`;
      const translateY = `${lastSnapPosition.current?.y ?? frame.translate[1] + pageScrollAdjustment[1] / zoom}px`;
      const rotate = `${frame.rotate}deg`;

      target.style.transform = `translate(${translateX}, ${translateY}) rotate(${rotate})`;
    };

    e.events.forEach((ev: OnDragEvent) => {
      if (ev) {
        const target = ev.target;
        const widgetId = target.id;
        const widgetRef = widgetRefs[widgetId];
        const customWidgetDrag = widgetRef ? widgetRef[WidgetEvent.onDrag] : null;
        e.target = target;

        /**
         * Members of a responsive widget should not be translated as their positions should be relative to the
         * parent container.
         * Only the parent should be translated.
         */
        if (groupIdCache.isResponsiveGroupMember(widgetId)) {
          return;
        }

        if (customWidgetDrag) {
          customWidgetDrag({
            event: ev,
            [WidgetEvent.onDrag]: onDragGroupWidget,
            target,
            isGroup: true,
          });
        } else {
          onDragGroupWidget(ev);
        }
      }
    });
  };

  const onDragGroupEnd = (e: OnDragGroupEndEvent): void => {
    resetActionState();

    let batchUpdateWidgetList: UpdateWidget[] = [];

    // SmartGuide
    hide();

    const onDragGroupEndWidget = (e: OnDragGroupEndEvent): void => {
      const target = e.target;
      const frame = frameMap.get(target);
      const pageScrollAdjustment = pageScrollOnDrag.current;
      const newLeftPx =
        parseStrictNumber(target.style.left) +
        (lastSnapPosition.current?.x ?? frame.translate[0] + pageScrollAdjustment[0] / zoom);
      const newTopPx =
        parseStrictNumber(target.style.top) +
        (lastSnapPosition.current?.y ?? frame.translate[1] + pageScrollAdjustment[1] / zoom);

      // Generate connected line data before updating the style
      const connectedLineDatas = getConnectedLines(target.id, target as HTMLElement);

      // Update style
      target.style.left = `${newLeftPx}px`;
      target.style.top = `${newTopPx}px`;
      frame.translate = [0, 0];

      // Reset transform value
      target.style.transform = `translate(${frame.translate[0]}px, ${frame.translate[1]}px) rotate(${frame.rotate}deg)`;

      if (checkShouldSaveTarget(activeWidgetIds, target)) {
        batchUpdateWidgetList.push({
          widgetId: target.id,
          widgetData: {
            leftPx: newLeftPx,
            topPx: newTopPx,
          },
        });
        batchUpdateWidgetList = batchUpdateWidgetList.concat(connectedLineDatas);
      }
    };

    const saveWidget = (widgetId: WidgetId, data: object) => {
      batchUpdateWidgetList.push({ widgetId, widgetData: data });
    };

    e.targets.forEach((target) => {
      const widgetId = target.id;
      const widgetRef = widgetRefs[widgetId];
      const customWidgetDragEnd = widgetRef ? widgetRef[WidgetEvent.onDragEnd] : null;
      e.target = target;

      if (customWidgetDragEnd) {
        customWidgetDragEnd({
          event: e,
          [WidgetEvent.onDragEnd]: onDragGroupEndWidget,
          target,
          saveWidget,
          isGroup: true,
        });
      } else {
        onDragGroupEndWidget(e);
      }
    });

    // Update the widget data for the connected lines attached to group widget.
    activeWidgets.forEach((widget) => {
      if (!!widget.groupId && widget.groupMembers.length > 1) {
        // get the group position of the groupId for this widget
        const groupPosition = getGroupPosition(e, frameMap, pageScrollOnDrag, zoom, widget.groupMembers);
        const widgetGroupId = widget.groupId;

        let originalTarget = document.querySelector(`[id="${widgetGroupId}"]:not(.moveable-area)`);

        if (originalTarget && groupPosition) {
          setWidgetElementWrapperPosition(widgetGroupId, {
            leftPx: groupPosition.x,
            topPx: groupPosition.y,
            widthPx: groupPosition.width,
            heightPx: groupPosition.height,
          });
          let originalTargetElement = originalTarget as HTMLElement;
          const connectedLineDatasForGroup = getConnectedLines(
            originalTargetElement.id,
            originalTargetElement as HTMLElement,
          );
          batchUpdateWidgetList = batchUpdateWidgetList.concat(connectedLineDatasForGroup);
        }
      }
    });

    pageScrollOnDrag.current = [0, 0];

    stopFlowMode();

    const { isDrag: isDragEvent } = e as OnDragGroupEndEvent;

    // update widget list only if event was drag event
    if (isDragEvent) {
      dispatch(updateWidget(batchUpdateWidgetList));
      dispatch(resetPreviousWidgetData());
    }

    // Render the port: line creator if its a single group and it has connected lines
    if (isActiveWidgetLinesConnected && activeWidgets.length === 1) {
      const widgetGroupId = activeWidgets[0].groupId;
      const responsiveGroupId = activeWidgets[0].responsiveGroupId;

      // if its a group widget always render the port for the group
      if (widgetGroupId) renderGroupPort(widgetGroupId);
      else if (responsiveGroupId) {
        // if its a responsive group widget render the port for a single widget
        const responsiveWidgetBaseEl = getElementForWidgetId(responsiveGroupId);
        renderPort(responsiveWidgetBaseEl, PortTypes.Creator);
      }
    }
  };

  const onResizeGroupStart = (e: OnResizeGroupStartEvent): void => {
    // Start observing of connected lines
    startFlowMode();

    dispatch(setWidgetActionState(WidgetActionState.Resize));

    // for groups side handles do not keep aspect ratio
    const isCorner = isCornerHandle(e.direction);
    if (isCorner) setKeepRatio(true);
    else setKeepRatio(false);

    const onResizeGroupStartWidget = (ev: OnResizeStartEvent): void => {
      const frame = getStartFrame(ev.target, frameMap);

      ev.dragStart && ev.dragStart.set(frame.translate);
    };

    const textWidgets: HTMLElement[] = [];
    const shapeWidgets: HTMLElement[] = [];

    e.events.forEach((ev: OnResizeStartEvent) => {
      const target = ev.target;
      const widgetId = target.id;
      const widgetRef = widgetRefs[widgetId];

      const customWidgetResizeStart = widgetRef ? widgetRef[WidgetEvent.onResizeStart] : null;
      if (customWidgetResizeStart) {
        customWidgetResizeStart({
          event: ev,
          [WidgetEvent.onResizeStart]: onResizeGroupStartWidget,
          frameMap,
          isGroup: true,
        });
      } else {
        onResizeGroupStartWidget(ev);
      }

      // For group side handle resizing - get the text and shape widgets
      if (!isCorner && hasMemberWidgets) createResponsiveWidgetArr(ev, textWidgets, shapeWidgets);
    });

    // PortRenderer
    clearPort();

    // the responsive group resize behavior is only for side handles
    if (isCorner) return;
    // if there is more than one shape widget -> no responsive resize behavior
    if (shapeWidgets.length !== 1) return;
    if (textWidgets.length === 0) return;
    responsiveShapeRef.current = createResponsiveShape(shapeWidgets, textWidgets);
  };

  // TODO: connect with smartguide
  const onResizeGroup = (e: OnResizeGroupEvent): void => {
    // NOTE: Prevent port rendering when Select+Drag
    if (creatorPortData !== null) clearPort();

    activeWidgets.forEach((widget) => {
      if (!!widget.groupId && widget.groupMembers.length > 1) {
        // get the group position of the groupId for this widget
        const groupPosition = getGroupPosition(e, frameMap, pageScrollOnDrag, zoom, widget.groupMembers);
        const widgetGroupId = widget.groupId;
        if (groupPosition) {
          setWidgetElementWrapperPosition(widgetGroupId, {
            leftPx: groupPosition.x,
            topPx: groupPosition.y,
            widthPx: groupPosition.width,
            heightPx: groupPosition.height,
          });
        }
      }
    });

    setHandleStyle(resizeStateRef, e.direction);

    const onResizeGroupWidget = (ev: OnResizeEvent) => {
      const target = ev.target;
      const frame = frameMap.get(target);

      frame.translate = ev.drag.beforeTranslate;

      // Ignore to update current widget style if value is a string(auto, fit-content, min-content, max-content, etc)
      if (isTargetStyleNumber(target.style.width)) target.style.width = `${ev.width}px`;
      if (isTargetStyleNumber(target.style.height)) target.style.height = `${ev.height}px`;

      // Member widgets of responsive groupd should not be translated as they are relative to the container
      if (groupIdCache.isResponsiveGroupMember(target.id)) return;

      const translateX = ev.drag.beforeTranslate[0];
      const translateY = ev.drag.beforeTranslate[1];
      ev.target.style.transform = `translate(${translateX}px, ${translateY}px) rotate(${frame.rotate}deg)`;
    };

    e.events.forEach((ev: OnResizeEvent) => {
      const target = ev.target;
      const widgetId = target.id;
      const widgetRef = widgetRefs[widgetId];

      const customWidgetResize = widgetRef ? widgetRef[WidgetEvent.onResize] : null;
      if (customWidgetResize) {
        customWidgetResize({
          event: ev,
          [WidgetEvent.onResize]: onResizeGroupWidget,
          frameMap,
          isGroup: true,
          isResponsiveGroup: groupIdCache.isResponsiveGroupMember(widgetId),
        });
      } else onResizeGroupWidget(ev);
    });

    // for group responsive side handle resizing - keep the shape wrapped around the text widgets
    // If there is no shape or no text widgets, this won't run
    const isSideResize = isSideHandle(e.direction);
    if (!isSideResize) return;
    if (!responsiveShapeRef.current?.shapeId) return;
    responsiveResizeGroupMembers(responsiveShapeRef.current);
  };

  const onResizeGroupEnd = (e: OnResizeGroupEndEvent): void => {
    unsetHandleStyle(resizeStateRef);
    resetActionState();

    let batchUpdateWidgetList: UpdateWidget[] = [];
    responsiveShapeRef.current = undefined;

    const onResizeGroupEndWidget = (e: OnResizeEndEvent, widgetData = {}) => {
      const target = e.target;
      const widgetId = target.id;
      const frame = frameMap.get(target);

      // Generate connected line data before updating the style
      const connectedLineDatas = getConnectedLines(widgetId, target as HTMLElement);

      const translateX = frame.translate[0];
      const hasFixedY =
        groupIdCache.isResponsiveWidgetBase(widgetId) && e.lastEvent && isSideHandle(e.lastEvent.direction);
      const translateY = hasFixedY ? 0 : frame.translate[1];

      const newLeftPx = parseStrictNumber(target.style.left) + translateX;
      const newTopPx = parseStrictNumber(target.style.top) + translateY;
      const newWidthPx = target.clientWidth;
      const newHeightPx = target.clientHeight;

      // Update style
      target.style.left = `${newLeftPx}px`;
      target.style.top = `${newTopPx}px`;

      // Ignore to update current widget style if value is a string(auto, fit-content, min-content, max-content, etc)
      if (isTargetStyleNumber(target.style.width)) target.style.width = `${newWidthPx}px`;
      if (isTargetStyleNumber(target.style.height)) target.style.height = `${newHeightPx}px`;

      // Reset transform value
      frame.translate = [0, 0];
      target.style.transform = `translate(${frame.translate[0]}px, ${frame.translate[1]}px) rotate(${frame.rotate}deg)`;

      if (checkShouldSaveTarget(activeWidgetIds, target)) {
        batchUpdateWidgetList.push({
          widgetId,
          widgetData: {
            leftPx: newLeftPx,
            topPx: newTopPx,
            widthPx: newWidthPx,
            heightPx: newHeightPx,
            ...widgetData,
          },
        });
        batchUpdateWidgetList = batchUpdateWidgetList.concat(connectedLineDatas);
      }
    };

    const saveWidget = (widgetId: WidgetId, data: object) => {
      batchUpdateWidgetList.push({ widgetId, widgetData: data });
    };

    e.targets.forEach((target: HTMLElement | SVGElement) => {
      const widgetId = target.id;
      const widgetRef = widgetRefs[widgetId];
      const customWidgetResizeEnd = widgetRef ? widgetRef[WidgetEvent.onResizeEnd] : null;
      e.target = target;

      if (customWidgetResizeEnd) {
        customWidgetResizeEnd({
          event: e,
          [WidgetEvent.onResizeEnd]: onResizeGroupEndWidget,
          target,
          saveWidget,
          isGroup: true,
        });
      } else onResizeGroupEndWidget(e);
    });

    // Update the widget data for the connected lines attached to group widget.
    activeWidgets.forEach((widget) => {
      if (!!widget.groupId && widget.groupMembers.length > 1) {
        // get the group position of the groupId for this widget
        const groupPosition = getGroupPosition(e, frameMap, pageScrollOnDrag, zoom, widget.groupMembers);
        const widgetGroupId = widget.groupId;

        let originalTarget = document.querySelector(`[id="${widgetGroupId}"]:not(.moveable-area)`);

        if (originalTarget && groupPosition) {
          let originalTargetElement = originalTarget as HTMLElement;
          originalTargetElement.style.left = `${groupPosition.x}px`;
          originalTargetElement.style.top = `${groupPosition.y}px`;
          originalTargetElement.style.width = `${groupPosition.width}px`;
          originalTargetElement.style.height = `${groupPosition.height}px`;

          const connectedLineDatasForGroup = getConnectedLines(
            originalTargetElement.id,
            originalTargetElement as HTMLElement,
          );
          batchUpdateWidgetList = batchUpdateWidgetList.concat(connectedLineDatasForGroup);
        }
      }
    });

    dispatch(updateWidget(batchUpdateWidgetList));

    stopFlowMode();

    // Render the port: line creator if its a single group and it has connected lines
    if (isActiveWidgetLinesConnected && activeWidgets.length === 1) {
      const widgetGroupId = activeWidgets[0].groupId;
      const responsiveGroupId = activeWidgets[0].responsiveGroupId;
      // if its a group widget always render the port for the group
      if (widgetGroupId) renderGroupPort(widgetGroupId);
      else if (responsiveGroupId) {
        // if its single a responsive group widget render the port for a single widget
        const responsiveWidgetBaseEl = getElementForWidgetId(responsiveGroupId);
        renderPort(responsiveWidgetBaseEl, PortTypes.Creator);
      }
    }
  };

  const onRotateGroupStart = (e: OnRotateGroupStartEvent): void => {
    // Start observing of connected lines
    startFlowMode();
    // PortRenderer
    clearPort();
    // Drag handle is hidden when the widget is rotated -> cursor style needs to be forced
    document.body.style.cursor = 'alias';

    dispatch(setWidgetActionState(WidgetActionState.Rotate));

    e.events.forEach((ev: OnRotateStartEvent, i) => {
      const frame = getStartFrame(ev.target, frameMap);

      ev.set(frame.rotate);
      ev.dragStart && ev.dragStart.set(frame.translate);
    });
  };

  const onRotateGroup = (e: OnRotateGroupEvent): void => {
    e.events.forEach((ev: OnRotateEvent, i) => {
      const target = ev.target;
      const frame = frameMap.get(target);

      frame.translate = ev.drag.beforeTranslate;
      frame.rotate = ev.rotate;

      ev.target.style.transform = `translate(${frame.translate[0]}px, ${frame.translate[1]}px) rotate(${ev.rotate}deg)`;
    });
  };

  const onRotateGroupEnd = (e: OnRotateGroupEndEvent): void => {
    document.body.style.cursor = '';
    resetActionState();

    let batchUpdateWidgetList: UpdateWidget[] = [];

    const onRotateGroupEndWidget = (e: OnRotateGroupEndEvent) => {
      const target = e.target;
      const frame = frameMap.get(target);
      const newLeftPx = parseStrictNumber(target.style.left) + frame.translate[0];
      const newTopPx = parseStrictNumber(target.style.top) + frame.translate[1];

      // Generate connected line data before updating the style
      const connectedLineDatas = getConnectedLines(target.id, target as HTMLElement);

      // Update style
      target.style.left = `${newLeftPx}px`;
      target.style.top = `${newTopPx}px`;
      frame.translate = [0, 0];

      // Reset transform value
      target.style.transform = `translate(${frame.translate[0]}px, ${frame.translate[1]}px) rotate(${frame.rotate}deg)`;

      if (checkShouldSaveTarget(activeWidgetIds, target)) {
        batchUpdateWidgetList.push({
          widgetId: target.id,
          widgetData: {
            leftPx: newLeftPx,
            topPx: newTopPx,
            rotateDeg: frame.rotate,
          },
        });
        batchUpdateWidgetList = batchUpdateWidgetList.concat(connectedLineDatas);
      }
    };

    const saveWidget = (widgetId: WidgetId, data: object) => {
      batchUpdateWidgetList.push({ widgetId, widgetData: data });
    };

    e.targets.forEach((target) => {
      const widgetId = target.id;
      const widgetRef = widgetRefs[widgetId];
      const customWidgetRotateEnd = widgetRef ? widgetRef[WidgetEvent.onRotateEnd] : null;
      e.target = target;

      if (customWidgetRotateEnd) {
        customWidgetRotateEnd({
          event: e,
          [WidgetEvent.onRotateEnd]: onRotateGroupEndWidget,
          target,
          saveWidget,
          isGroup: true,
        });
      } else onRotateGroupEndWidget(e);
    });

    dispatch(updateWidget(batchUpdateWidgetList));

    stopFlowMode();
  };

  const isDraggable = !isLocked;
  const isEdgeDraggable = getWidgetEdgeDraggable(activeWidgetIds) && isDraggable;

  // Disable rotation for flowchart widgets if multiple are selected - not yet supported
  const isRotatable =
    getWidgetRotatable(activeWidgetIds) &&
    (activeWidgetIds.length === 1 || (!isActiveWidgetLinesConnected && !isActiveWidgetsHasLineConnected));

  const extendWidget = extendWidgetCreator({
    activeWidgetIds,
    widgetRefs,
    frameMap,
    compute,
    match,
    hide,
    saveWidget,
    resetActionState,
    setHandleStyle,
    unsetHandleStyle,
    resizeStateRef,
  });

  return (
    <>
      <BoundingBoxBase
        moveableRef={moveableRef}
        widgetHandles={widgetHandles}
        isLocked={isLocked}
        isDraggable={isDraggable}
        isResizable={isResizable}
        isRotatable={isRotatable}
        keepRatio={keepRatio}
        hideDefaultLines={hideDefaultLines}
        isActiveWidgetLinesConnected={isActiveWidgetLinesConnected}
        activeGroupIdx={activeGroupIdx}
        onDragStart={extendWidget(onDragStart, WidgetEvent.onDragStart)}
        onDrag={extendWidget(onDrag, WidgetEvent.onDrag)}
        onDragEnd={extendWidget(onDragEnd, WidgetEvent.onDragEnd)}
        onResizeStart={extendWidget(onResizeStart, WidgetEvent.onResizeStart)}
        onResize={extendWidget(onResize, WidgetEvent.onResize)}
        onResizeEnd={extendWidget(onResizeEnd, WidgetEvent.onResizeEnd)}
        onRotateStart={extendWidget(onRotateStart, WidgetEvent.onRotateStart)}
        onRotate={extendWidget(onRotate, WidgetEvent.onRotate)}
        onRotateEnd={extendWidget(onRotateEnd, WidgetEvent.onRotateEnd)}
        onClickGroup={onClickGroup}
        onDragGroupStart={onDragGroupStart}
        onDragGroup={onDragGroup}
        onDragGroupEnd={onDragGroupEnd}
        onResizeGroupStart={onResizeGroupStart}
        onResizeGroup={onResizeGroup}
        onResizeGroupEnd={onResizeGroupEnd}
        onRotateGroupStart={onRotateGroupStart}
        onRotateGroup={onRotateGroup}
        onRotateGroupEnd={onRotateGroupEnd}
        hasMemberWidgets={hasMemberWidgets}
        showDragControl={showDragControl}
        padding={padding}
        widgetType={widgetType}
        isEdgeDraggable={isEdgeDraggable}
        getPageScrollPosition={getPageScrollPosition}
        onScroll={onScroll}
        onScrollGroup={onScrollGroup}
        selectoRef={selectoRef}
      />
      <UpdateBoundingRect setSmartGuideConfig={setSmartGuideConfig} moveableRef={moveableRef} />
    </>
  );
};

// The base bounding box component that handles the moveable events
// This is a separate component optimized for to keep the target state
// as this state changes frequently while drag-selecting widgets
const BoundingBoxBase = ({
  moveableRef,
  widgetHandles,
  isLocked,
  isDraggable,
  isResizable,
  isRotatable,
  keepRatio,
  hideDefaultLines,
  isActiveWidgetLinesConnected,
  activeGroupIdx,
  onDragStart,
  onDrag,
  onDragEnd,
  onResizeStart,
  onResize,
  onResizeEnd,
  onRotateStart,
  onRotate,
  onRotateEnd,
  onClickGroup,
  onDragGroupStart,
  onDragGroup,
  onDragGroupEnd,
  onResizeGroupStart,
  onResizeGroup,
  onResizeGroupEnd,
  onRotateGroupStart,
  onRotateGroup,
  onRotateGroupEnd,
  hasMemberWidgets,
  showDragControl,
  padding,
  widgetType,
  isEdgeDraggable,
  getPageScrollPosition,
  onScroll,
  onScrollGroup,
  selectoRef,
}: BoundingBoxBaseProps) => {
  // The HTML targets that are being observed by moveable as bounding boxes
  const [targets, setTargets] = useState<HTMLElement[]>([]);

  const { widgetRefs } = useBoundingBox();
  const { pageScrollContainerRef } = useFocus();

  const cropId = useAppSelector(selectCropId);
  const activeWidgetIds = useAppSelector(selectActiveWidgetIds);
  const widgetActionState = useAppSelector(selectWidgetActionState);

  // While drag-selecting widgets this is set to true
  const isDragAreaSelectRef = useRef(false);

  // set the bounding box targets if widgets are selected programatically
  // (e.g. dispatch(setActiveWidgetIds([id]))
  useLayoutEffect(() => {
    if (!activeWidgetIds || !activeWidgetIds.length) return setTargets([]);

    const widgetEls = getWidgetElements(activeWidgetIds, widgetRefs);
    setTargets(widgetEls);
  }, [activeWidgetIds, widgetRefs]);

  // The target idxs from widgetElements that are not included in activeWidgetIds
  // while drag-selecting widgets we don't want to include the active widgets as targets
  const nonActiveTargetIdxs = isDragAreaSelectRef.current ? [] : getNonActiveTargetIdxs(targets, activeWidgetIds);

  return (
    <>
      <StyledMoveable
        flushSync={flushSync}
        ref={moveableRef}
        // crop has its own target so we don't need to pass any targets here
        target={cropId ? [] : targets}
        renderDirections={widgetHandles}
        className={isLocked ? MOVEABLE_LOCK_CLASS : ''}
        // boolean options
        origin={false}
        draggable={isDraggable}
        resizable={isResizable && !isLocked}
        rotatable={isRotatable && !isLocked}
        rotationPosition='bottom'
        keepRatio={keepRatio}
        hideDefaultLines={hideDefaultLines}
        positionLowerDragResizeControl={isActiveWidgetLinesConnected}
        edgeDraggable={true}
        // single widget
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragEnd={onDragEnd}
        onResizeStart={onResizeStart}
        onResize={onResize}
        onResizeEnd={onResizeEnd}
        onRotateStart={onRotateStart}
        onRotate={onRotate}
        onRotateEnd={onRotateEnd}
        // widget group
        onClickGroup={onClickGroup}
        onDragGroupStart={onDragGroupStart}
        onDragGroup={onDragGroup}
        onDragGroupEnd={onDragGroupEnd}
        onResizeGroupStart={onResizeGroupStart}
        onResizeGroup={onResizeGroup}
        onResizeGroupEnd={onResizeGroupEnd}
        onRotateGroupStart={onRotateGroupStart}
        onRotateGroup={onRotateGroup}
        onRotateGroupEnd={onRotateGroupEnd}
        // custom styling override options
        activeGroupIdx={activeGroupIdx}
        isGroup={hasMemberWidgets}
        // don't show rotation controls when in widget action or when lines connected
        showControls={!!widgetActionState}
        showDragControl={showDragControl}
        invisibleBoundingBox={widgetType === WidgetType.Line}
        padding={padding}
        // Set pointer events on edge
        isEdgeDraggable={isEdgeDraggable}
        // scroll options
        scrollable={true}
        scrollContainer={pageScrollContainerRef?.current}
        scrollThreshold={PAGE_SCROLL_THRESHOLD}
        getScrollPosition={getPageScrollPosition}
        onScroll={onScroll}
        onScrollGroup={onScrollGroup}
        checkResizableError={false}
        nonActiveTargetIdxs={nonActiveTargetIdxs}
      />
      <WidgetSelect
        selectoRef={selectoRef}
        moveableRef={moveableRef}
        setTargets={setTargets}
        isDragAreaSelectRef={isDragAreaSelectRef}
      />
    </>
  );
};
